import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { MessageCircle, Send, Heart, Users } from 'lucide-react';
import { tribalSocialAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';

const PeacePipeMessage = ({ 
  receiverId, 
  receiverName = 'Unknown User',
  trigger = null,
  onMessageSent = null 
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const { toast } = useToast();

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const response = await tribalSocialAPI.getMessageTemplates();
      setTemplates(response.templates || []);
    } catch (error) {
      console.error('Error loading templates:', error);
    }
  };

  const handleSendMessage = async () => {
    if (!receiverId || (!selectedTemplate && !customMessage.trim())) return;
    
    setLoading(true);
    try {
      let result;
      const messageContent = customMessage || 'Greetings from a fellow community member.';
      
      if (selectedTemplate === 'peace_pipe') {
        result = await tribalSocialAPI.sendPeacePipe(receiverId, messageContent);
      } else if (selectedTemplate === 'kinship_request') {
        result = await tribalSocialAPI.sendKinshipRequest(receiverId, messageContent);
      } else {
        result = await tribalSocialAPI.sendMessage(receiverId, messageContent, selectedTemplate || 'text');
      }
      
      if (result.success) {
        toast({
          title: "Message Sent!",
          description: `Your message has been sent to ${receiverName}`,
        });
        setOpen(false);
        setCustomMessage('');
        setSelectedTemplate('');
        onMessageSent?.(result);
      } else {
        throw new Error(result.message || 'Failed to send message');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to send message",
      });
    } finally {
      setLoading(false);
    }
  };

  const getTemplateIcon = (type) => {
    switch (type) {
      case 'peace_pipe':
        return '🕊️';
      case 'kinship_request':
        return '🤝';
      case 'ceremony_invite':
        return '🔥';
      default:
        return '💬';
    }
  };

  const defaultTrigger = (
    <Button variant="outline" size="sm">
      <MessageCircle className="w-4 h-4 mr-2" />
      Send Peace Pipe
    </Button>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || defaultTrigger}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <MessageCircle className="w-5 h-5 mr-2" />
            Send Message to {receiverName}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Cultural Message Templates */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">
              Choose a Cultural Greeting
            </label>
            <Select value={selectedTemplate || 'custom'} onValueChange={(value) => setSelectedTemplate(value === 'custom' ? '' : value)}>
              <SelectTrigger>
                <SelectValue placeholder="Select a message type..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="custom">Custom Message</SelectItem>
                {templates.map((template) => (
                  <SelectItem key={template.type} value={template.type}>
                    <div className="flex items-center">
                      <span className="mr-2">{template.icon}</span>
                      {template.title}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Template Preview */}
          {selectedTemplate && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="flex items-center mb-2">
                <span className="text-lg mr-2">
                  {getTemplateIcon(selectedTemplate)}
                </span>
                <Badge variant="outline" className="text-amber-800">
                  {templates.find(t => t.type === selectedTemplate)?.title}
                </Badge>
              </div>
              <div className="text-sm text-amber-800">
                <div className="font-medium mb-1">Template Preview:</div>
                <div className="text-xs bg-white rounded p-2 border">
                  {templates.find(t => t.type === selectedTemplate)?.sample_content}
                </div>
              </div>
            </div>
          )}

          {/* Custom Message */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">
              {selectedTemplate ? 'Your Personal Message' : 'Your Message'}
            </label>
            <Textarea
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              placeholder={
                selectedTemplate 
                  ? "Add your personal message here (will be included in the template)..." 
                  : "Write your message..."
              }
              rows={4}
              className="resize-none"
            />
          </div>

          {/* Cultural Context */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-start">
              <Heart className="w-4 h-4 text-blue-600 mr-2 mt-0.5" />
              <div className="text-sm text-blue-800">
                <div className="font-medium mb-1">Cultural Connection</div>
                <div className="text-xs">
                  Traditional greetings strengthen the bonds between community members 
                  and honor our ancestral ways of communication.
                </div>
              </div>
            </div>
          </div>

          {/* Send Button */}
          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSendMessage}
              disabled={loading || (!selectedTemplate && !customMessage.trim())}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              {loading ? 'Sending...' : 'Send Message'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PeacePipeMessage;